import AdminContent from '@/application/components/sheet/AdminContent'
import AdminHeader from '@/application/components/sheet/AdminHeader'
import ApplicationFooter from '@/application/components/sheet/Footer'
import SellerContent from '@/application/components/sheet/SellerContent'
import SellerHeader from '@/application/components/sheet/SellerHeader'
import { useNetSeller } from '@/application/hooks/useNetSeller'
import { getMaskedDisplayName } from '@almaris/shared/helpers/getMaskedDisplayName'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { EstateForSalePageFragment, WebappApplicationFragment } from '@gql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ApplicationAcceptationActions } from './ApplicationAcceptationActions'

type ApplicationSheetProps = {
  application: WebappApplicationFragment
  estate: EstateForSalePageFragment
  onNext: (() => void) | null
  onPrevious: (() => void) | null
  isAdmin: boolean
  someApplicationIsAccepted: boolean
} & React.ComponentProps<typeof Sheet>

const ApplicationSheet = ({
  application,
  estate,
  onNext,
  onPrevious,
  isAdmin,
  someApplicationIsAccepted,
  ...sheetProps
}: ApplicationSheetProps) => {
  const { t } = useTranslation()
  const shouldHideBuyerContactInfo =
    application.estate.seller.hideBuyerContactInfo
  const sellerCandidateName = shouldHideBuyerContactInfo
    ? getMaskedDisplayName({
        displayName: application.buyer_member.user?.displayName,
      }) || t('ApplicationSheet.notDefined')
    : application.buyer_member.user?.displayName ??
      application.buyer_member.user?.email ??
      t('ApplicationSheet.notDefined')

  const netSeller = useNetSeller({
    offerAmount: application.offerAmount ?? null,
    fixedFee: estate.mandate?.fixedFee ?? 0,
    proportionalFeesRate: estate.mandate?.proportionalFeesRate ?? 0,
    feeVATRate: estate.mandate?.feeVATRate ?? 0,
    fixedFeeThreshold: estate.mandate?.fixedFeeThreshold ?? 0,
  })

  return (
    <Sheet {...sheetProps}>
      <SheetContent className="tw-flex tw-flex-col tw-gap-6">
        <SheetTitle />
        <SheetHeader>
          {isAdmin && (
            <AdminHeader
              onNext={onNext}
              onPrevious={onPrevious}
              propertyAddress={estate.address}
            />
          )}
          {!isAdmin && <SellerHeader candidatName={sellerCandidateName} />}
        </SheetHeader>
        <Separator />
        <ScrollArea className="tw-h-[calc(100vh-100px)] tw-w-full tw-px-9">
          {isAdmin && (
            <AdminContent application={application} netSeller={netSeller} />
          )}
          {!isAdmin && (
            <SellerContent application={application} netSeller={netSeller} />
          )}
        </ScrollArea>
        <Separator />
        <ApplicationFooter application={application} />
        <ApplicationAcceptationActions
          application={application}
          isAdmin={isAdmin}
          someApplicationIsAccepted={someApplicationIsAccepted}
        />
      </SheetContent>
    </Sheet>
  )
}

export default ApplicationSheet
